<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'CSAT por Agente',
  },
});

const sortedAgents = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data]
    .filter(agent => agent.total_responses > 0)
    .sort((a, b) => b.satisfaction_score - a.satisfaction_score);
});

const avgSatisfaction = computed(() => {
  if (sortedAgents.value.length === 0) return 0;
  const total = sortedAgents.value.reduce(
    (sum, a) => sum + a.satisfaction_score,
    0
  );
  return (total / sortedAgents.value.length).toFixed(1);
});

function getScoreColor(score) {
  if (score >= 80) return 'text-n-teal-11';
  if (score >= 60) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getBarColor(score) {
  if (score >= 80) return 'bg-n-teal-9';
  if (score >= 60) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function getEmoji(rating) {
  const emojis = { 1: '😠', 2: '😞', 3: '😐', 4: '😊', 5: '😍' };
  return emojis[rating] || '⭐';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-star text-lg text-n-amber-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">Media geral:</span>
        <span class="text-lg font-bold" :class="getScoreColor(avgSatisfaction)">
          {{ avgSatisfaction }}%
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedAgents.length === 0"
      class="flex flex-col items-center justify-center h-64 text-n-slate-11"
    >
      <span class="i-lucide-star-off text-4xl mb-2 opacity-50" />
      <span class="text-sm">Sem avaliacoes no periodo</span>
    </div>

    <!-- Agent List -->
    <div v-else class="flex flex-col gap-3 max-h-96 overflow-y-auto">
      <div
        v-for="(agent, index) in sortedAgents"
        :key="agent.agent_id"
        class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
      >
        <span class="text-sm font-medium text-n-slate-10 w-6">{{
          index + 1
        }}</span>

        <Avatar
          :name="agent.agent_name"
          :src="agent.thumbnail"
          :size="36"
          rounded-full
        />

        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ agent.agent_name }}
            </span>
            <span
              class="text-lg font-bold"
              :class="getScoreColor(agent.satisfaction_score)"
            >
              {{ agent.satisfaction_score }}%
            </span>
          </div>

          <!-- Progress bar -->
          <div class="flex items-center gap-2">
            <div class="flex-1 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="getBarColor(agent.satisfaction_score)"
                :style="{ width: `${agent.satisfaction_score}%` }"
              />
            </div>
          </div>

          <!-- Rating distribution -->
          <div class="flex items-center justify-between mt-2 text-xs">
            <span class="text-n-slate-10">{{ agent.total_responses }} avaliacoes</span>
            <div class="flex items-center gap-1">
              <span
                v-for="rating in [5, 4, 3, 2, 1]"
                :key="rating"
                class="flex items-center gap-0.5"
                :title="`${rating} estrelas: ${agent.distribution?.[rating] || 0}`"
              >
                <span class="text-xs">{{ getEmoji(rating) }}</span>
                <span class="text-n-slate-11">{{
                  agent.distribution?.[rating] || 0
                }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
